import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiError, downloadFile } from "@/lib/api";

const SAIL = "SAIL";

/** The team report to print — one unit, or SAIL in total for SDTD — as Word or Excel. */
export const TeamReportDialog = ({ data, unit }) => {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("");
  const [busy, setBusy] = useState("");
  const fallback = unit || (data.can_edit_all ? SAIL : data.plants[0]?.code || "");
  useEffect(() => {
    if (open) setScope(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const get = async (fmt) => {
    setBusy(fmt);
    try {
      const picked = scope === SAIL ? "" : scope;
      await downloadFile("/people/report", { fmt, unit: picked }, `SAIL_Team_Report_${scope}.${fmt}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const btn = "h-12 flex-1 rounded-none font-display text-xs font-bold uppercase tracking-widest";
  return (
    <>
      <Button
        variant="outline"
        className="h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
        onClick={() => setOpen(true)}
        data-testid="team-report-button"
      >
        <Printer className="mr-2 h-4 w-4" /> Team report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="team-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black">Team report</DialogTitle>
            <DialogDescription>
              Team strength by role, then everyone with their designation, department, e-mail and mobile —
              laid out to print on A4 landscape. Categories switched off on the portal are left out.
            </DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="label-caps">Report for</span>
            <Select value={scope || undefined} onValueChange={setScope}>
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="team-report-scope">
                <SelectValue placeholder="Choose the unit" />
              </SelectTrigger>
              <SelectContent className="max-h-72 rounded-none bg-popover">
                {data.can_edit_all && (
                  <SelectItem value={SAIL} data-testid="team-report-scope-option-SAIL">
                    SAIL — all units
                  </SelectItem>
                )}
                {data.plants.map((p) => (
                  <SelectItem key={p.code} value={p.code} data-testid={`team-report-scope-option-${p.code}`}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className="flex gap-2">
            <Button className={btn} disabled={!scope || !!busy} onClick={() => get("docx")} data-testid="team-report-docx">
              {busy === "docx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              Word (.docx)
            </Button>
            <Button variant="outline" className={btn} disabled={!scope || !!busy} onClick={() => get("xlsx")} data-testid="team-report-xlsx">
              {busy === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              Excel (.xlsx)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
